import dayjs from "dayjs";
import { Device } from "../../../model/Device";
import { IAssetTypeGroup } from "../services/assetTypeGroupsService";
import { IInspectionLog, IInspectionTemplateDetails } from "../store/types";
import { LocalizerService } from "../../../utils/localizer";
import { secondsToHours } from "../../../utils/helpers";
import constants from "../../../constants";

/**
 * Format inspection date and time
 * @param date Date object or ISO string
 * @returns Formatted string like "Nov 5, 2025, 11:42 am"
 */
export function formatInspectionDateTime(date: Date | string): string {
    return dayjs(date).format("MMM D, YYYY, h:mm a");
}

/**
 * Format a date as a neutral timestamp: "Sep 14, 2026 at 10:34 AM".
 *
 * Separate from formatInspectionDateTime, which every other inspection surface uses — this one
 * exists for the recertification step, where the date is presented as plain context rather than as
 * a completion event, and changing the shared format would move text on pages nobody asked about.
 */
export function formatTimestamp(date: Date | string): string {
    return dayjs(date).format("MMM D, YYYY [at] h:mm A");
}

/**
 * Completion time of an inspection log in epoch milliseconds. Falls back to
 * createdDateTime for logs submitted offline; 0 when the log has no usable date.
 */
export function getInspectionLogTime(log: IInspectionLog): number {
    const date = log.submittedDateTime ?? log.createdDateTime;
    return date ? new Date(date).getTime() : 0;
}

/**
 * Get asset identifier value (VIN or Serial Number)
 * @returns The identifier value, or empty string if none
 */
export function getAssetIdValue(device: Device): string {
    return device.vehicleIdentificationNumber || device.serialNumber || "";
}

/**
 * Get formatted distance value using LocalizerService
 * @param distanceKm Distance in kilometers
 * @param localizerService LocalizerService instance for locale-aware formatting
 * @returns Formatted distance string like "5 km" or "3 mi"
 */
export function getDistanceValue(distanceKm: number, localizerService: LocalizerService): string {
    const distanceInMeters = distanceKm * 1000;
    return localizerService.toLocalDistanceString(distanceInMeters, true);
}

/**
 * Format vehicle make/model/year from VIN info
 * @returns Formatted string like "Toyota Camry 2026" or empty string if no data
 */
export function formatMakeModelYear(device: Device): string {
    return [device.vinInfoMake, device.vinInfoModel, device.vinInfoYear]
        .filter(Boolean)
        .join(" ");
}

/**
 * Create a Map for O(1) lookup of asset type names by group ID
 *
 * @param availableAssetTypes - List of available asset type groups
 * @returns Map of group ID to asset type name
 */
export function createAssetTypeMap(
    availableAssetTypes: readonly IAssetTypeGroup[]
): Map<string, string> {
    return new Map(
        availableAssetTypes.map((type) => [type.id, type.name])
    );
}

/**
 * Built-in asset-type groups, in the order they win when a device belongs to more than one asset-type
 * group. Vehicle and Trailer come first so a device that is partly either is never resolved to a type
 * that skips the mandatory inspection-type selection.
 */
const BUILT_IN_ASSET_TYPE_PRIORITY = [
    constants.GROUPS.VEHICLE_GROUP_ID,
    constants.GROUPS.TRAILER_GROUP_ID,
    constants.GROUPS.EQUIPMENT_GROUP_ID,
    constants.GROUPS.CONTAINER_GROUP_ID
];

/**
 * Determine the asset-type group a device belongs to using pre-created Map
 *
 * Returns the group's ID along with its name because names are not unique — a database can hold the
 * built-in "Trailer" and a customer-created "Trailer" side by side. The ID is what identifies the
 * device's asset type; the name is for display only.
 *
 * MyGeotab limits an asset to one asset type, but older assets can still carry several. Those resolve
 * deterministically: built-in groups first, in BUILT_IN_ASSET_TYPE_PRIORITY order, then the first
 * custom group in the device's group order.
 *
 * @param device - Device object with groups array
 * @param assetTypeMap - Pre-created Map from createAssetTypeMap()
 * @returns The device's asset-type group (e.g., { id: "GroupTrailerId", name: "Trailer" }) or null if no match found
 */
export function getAssetTypeFromDevice(
    device: { readonly groups?: readonly { readonly id: string }[] },
    assetTypeMap: Map<string, string>
): IAssetTypeGroup | null {
    if (!device.groups?.length) {
        return null;
    }

    const deviceGroupIds = new Set(device.groups.map(group => group.id));
    for (const id of BUILT_IN_ASSET_TYPE_PRIORITY) {
        const name = assetTypeMap.get(id);
        if (name && deviceGroupIds.has(id)) {
            return { id, name };
        }
    }

    for (const group of device.groups) {
        const name = assetTypeMap.get(group.id);
        if (name) {
            return { id: group.id, name };
        }
    }

    return null;
}

/**
 * Asset-type group ID of an inspection asset.
 *
 * Prefers the ID resolved when the asset was built. Falls back to resolving from the device's groups
 * for assets persisted before assetTypeId existed — never to a name match, which is ambiguous.
 */
export function getAssetTypeId(
    asset: { readonly device: Parameters<typeof getAssetTypeFromDevice>[0]; readonly assetTypeId?: string },
    assetTypeMap: Map<string, string>
): string | undefined {
    return asset.assetTypeId ?? getAssetTypeFromDevice(asset.device, assetTypeMap)?.id;
}

/**
 * Returns true if the custom property item should be shown for the given device.
 *
 * A custom property is considered applicable when:
 *  - Its propertySet.applicableAssetTypeGroups is absent or empty (no asset-type restriction — show for all)
 *  - OR at least one group ID in propertySet.applicableAssetTypeGroups matches one of the device's group IDs
 *
 * A plain ID comparison is enough because the backend pre-expands the property's scope to every
 * group a device could belong to and still be in scope (see IInspectionItem.customProperty.propertySet),
 * so no ancestry resolution is needed offline. This lets a union template (Vehicle + Trailer) show
 * only the custom properties relevant to the current asset.
 */
export function isCustomPropertyApplicableToDevice(
    propertySetGroups: { id: string }[] | undefined,
    deviceGroupIds: Set<string>
): boolean {
    if (!propertySetGroups || propertySetGroups.length === 0) {
        return true;
    }
    return propertySetGroups.some(g => deviceGroupIds.has(g.id));
}

/**
 * Builds the set of inspection item IDs that should be skipped for this device because the
 * underlying custom property does not apply to its asset type.
 *
 * Both the inspection form and the review/submit step derive this from the same function: the items
 * hidden from the driver, the items excluded from validation, and the items omitted from the payload
 * must be the same set, or a driver is blocked by an error on a field they were never shown.
 */
export function buildSkipItemIds(
    templateDetails: IInspectionTemplateDetails,
    device: { groups?: { id?: string }[] } | undefined
): Set<string> {
    if (!device?.groups?.length) {
        return new Set<string>();
    }

    const deviceGroupIds = new Set<string>();
    for (const group of device.groups) {
        if (group.id) { deviceGroupIds.add(group.id); }
    }

    const skipItemIds = new Set<string>();
    for (const part of templateDetails.inspectionParts ?? []) {
        for (const item of part.inspectionItems ?? []) {
            if (item.itemType === "CustomProperty" && !isCustomPropertyApplicableToDevice(item.customProperty?.propertySet?.applicableAssetTypeGroups, deviceGroupIds)) {
                skipItemIds.add(item.id);
            }
        }
    }
    return skipItemIds;
}

/**
 * Format license plate with optional state abbreviation
 * @returns Formatted string like "ABC-123 (CA)" or empty string if no plate
 */
export function formatLicensePlate(device: Device): string {
    if (!device.licensePlate) {
        return "";
    }
    if (device.licenseState) {
        return `${ device.licensePlate } (${ device.licenseState })`;
    }
    return device.licensePlate;
}

/**
 * Format odometer value in meters to locale-aware distance string
 * @param odometerInMeters Odometer value in meters from CalculateHosOdometer API
 * @param localizerService LocalizerService instance for locale-aware formatting
 * @returns Formatted distance string or empty string if value is invalid
 */
export function formatOdometer(odometerInMeters: number | null | undefined, localizerService: LocalizerService): string {
    if (!odometerInMeters || odometerInMeters <= 0) {
        return "";
    }
    return localizerService.toLocalDistanceString(odometerInMeters);
}

/**
 * Format engine hours from seconds to hours string
 * @param engineHoursInSeconds Engine hours value in seconds from StatusData API
 * @returns Formatted hours string like "1250.5 h" or empty string if value is invalid
 */
export function formatEngineHours(engineHoursInSeconds: number | null | undefined): string {
    if (!engineHoursInSeconds || engineHoursInSeconds <= 0) {
        return "";
    }
    return `${ secondsToHours(engineHoursInSeconds).toFixed(1) } h`;
}
